export const SUMMONER_SPELLS = {
  flash: { id: 'flash', name: 'Flash', abbr: 'Fl', cooldown: 265, range: 400 },
  ignite: { id: 'ignite', name: 'Ignite', abbr: 'Ig', cooldown: 210, range: 600 },
  smite: { id: 'smite', name: 'Smite', abbr: 'Sm', cooldown: 70, range: 625 },
  heal: { id: 'heal', name: 'Heal', abbr: 'He', cooldown: 270 },
  exhaust: { id: 'exhaust', name: 'Exhaust', abbr: 'Ex', cooldown: 210, range: 550 },
  ghost: { id: 'ghost', name: 'Ghost', abbr: 'Gh', cooldown: 180 },
  teleport: { id: 'teleport', name: 'Teleport', abbr: 'Tp', cooldown: 300 },
  cleanse: { id: 'cleanse', name: 'Cleanse', abbr: 'Cl', cooldown: 210 },
  clarity: { id: 'clarity', name: 'Clarity', abbr: 'Cr', cooldown: 180 },
  barrier: { id: 'barrier', name: 'Barrier', abbr: 'Ba', cooldown: 210 }
};

/** AB-10: no jungle execute / channel TP — reject cast; ARAM also bans select. */
export const STUB_SUMMONER_SPELL_IDS = new Set([
  'teleport', 'smite', 'clairvoyance', 'surge', 'promote', 'revive'
]);

export function isStubSummonerSpell(spellId) {
  return STUB_SUMMONER_SPELL_IDS.has(spellId);
}

function castFlash(spell, caster, target, options = {}) {
  if (!target || target.x === undefined || target.y === undefined) {
    return { success: false, reason: 'INVALID_TARGET' };
  }
  const range = spell.range * (options.worldScale ?? 1);
  const dx = target.x - caster.x;
  const dy = target.y - caster.y;
  const dist = Math.hypot(dx, dy);
  let newX = target.x;
  let newY = target.y;
  if (dist > range && dist > 0) {
    newX = caster.x + (dx / dist) * range;
    newY = caster.y + (dy / dist) * range;
  }
  return {
    success: true,
    cooldown: spell.cooldown,
    caster: { ...caster, x: newX, y: newY }
  };
}

function castIgnite(spell, caster) {
  const level = caster.level || 1;
  return {
    success: true,
    cooldown: spell.cooldown,
    damage: 50 + (level * 20),
    damageType: 'true',
    grievousWounds: 0.5,
    dotDuration: 5
  };
}

function castHeal(spell, caster) {
  const level = caster.level || 1;
  const heal = Math.round(145 + (level - 1) * ((470 - 145) / 17));
  return {
    success: true,
    cooldown: spell.cooldown,
    heal,
    allyHealRatio: 0.5
  };
}

function castGhost(spell) {
  return {
    success: true,
    cooldown: spell.cooldown,
    buff: {
      id: 'ghost',
      duration: 10,
      msPercent: 0.27,
      ignoreUnitCollision: true
    }
  };
}

function castBarrier(spell, caster) {
  const level = caster.level || 1;
  return {
    success: true,
    cooldown: spell.cooldown,
    shield: 95 + (level - 1) * 20,
    shieldDuration: 2
  };
}

function castExhaust(spell) {
  return {
    success: true,
    cooldown: spell.cooldown,
    targetDebuff: {
      id: 'exhaust',
      duration: 2.5,
      msSlow: 0.3,
      damageReduction: 0.3,
      asReduction: 0.5
    }
  };
}

function castCleanse(spell) {
  return {
    success: true,
    cooldown: spell.cooldown,
    removeCrowdControl: true,
    buff: {
      id: 'cleanse_tenacity',
      duration: 3,
      tenacity: 0.65
    }
  };
}

function castClarity(spell) {
  return {
    success: true,
    cooldown: spell.cooldown,
    manaRestoreRatio: 0.4,
    allyManaRatio: 0.4
  };
}

const HANDLERS = {
  flash: castFlash,
  ignite: castIgnite,
  heal: castHeal,
  ghost: castGhost,
  barrier: castBarrier,
  exhaust: castExhaust,
  cleanse: castCleanse,
  clarity: castClarity
};

export function castSummonerSpell(spellId, caster, target, options = {}) {
  const spell = SUMMONER_SPELLS[spellId];
  if (!spell) return { success: false, reason: 'INVALID_SPELL' };
  if (isStubSummonerSpell(spellId)) {
    return { success: false, reason: 'STUB_SPELL', spellId };
  }
  const handler = HANDLERS[spellId];
  if (!handler) return { success: false, reason: 'STUB_SPELL', spellId };
  return handler(spell, caster, target, options);
}

/** HUD slot view-model for D/F (P3-32). Pure — no DOM. */
export function buildSummonerHudSlot(spellId, remainingCd = 0) {
  const spell = SUMMONER_SPELLS[spellId];
  if (!spell) return null;
  return {
    id: spell.id,
    name: spell.name,
    abbr: spell.abbr,
    cooldown: Math.max(0, remainingCd),
    maxCooldown: spell.cooldown
  };
}
